import type { ExperimentReportSelect, UserSummary } from 'types';
import type { REPORT_EXPECTATIONS, REPORT_NEXT_STEPS, REPORT_OUTSIDE_FACTORS } from 'definitions';
import type { ReportReadRow } from 'definitions/report-read';

export type ReportExpectation = (typeof REPORT_EXPECTATIONS)[number];
export type ReportNextStep = (typeof REPORT_NEXT_STEPS)[number];
export type ReportOutsideFactor = (typeof REPORT_OUTSIDE_FACTORS)[number];

/** The saved report as the API returns it: the row plus its last author, for the byline's badge and name. */
export type SavedReport = ExperimentReportSelect & { author: UserSummary | null };

type Choice<T extends string> = { value: T; label: string };

export const EXPECTATION_CHOICES: readonly Choice<ReportExpectation>[] = [
  { value: 'as-expected', label: 'As expected' },
  { value: 'partly', label: 'Partly' },
  { value: 'opposite', label: 'The opposite' },
  { value: 'no-clear-result', label: 'No clear result' },
];

export const NEXT_STEP_CHOICES: readonly Choice<ReportNextStep>[] = [
  { value: 'ship-winner', label: 'Ship the winner' },
  { value: 'iterate', label: 'Iterate' },
  { value: 'retest', label: 'Retest' },
  { value: 'drop-idea', label: 'Drop the idea' },
  { value: 'undecided', label: 'Not decided yet' },
];

export const OUTSIDE_FACTOR_CHOICES: readonly Choice<ReportOutsideFactor>[] = [
  { value: 'promotion', label: 'A promotion or campaign' },
  { value: 'seasonality', label: 'Seasonality' },
  { value: 'traffic-change', label: 'A traffic change' },
  { value: 'site-release', label: 'A site release' },
  { value: 'tracking-issue', label: 'A tracking issue' },
  { value: 'other', label: 'Something else' },
];

export const labelOf = <T extends string>(choices: readonly Choice<T>[], value: T): string =>
  choices.find((choice) => choice.value === value)?.label ?? value;

/**
 * The end-of-test modal's three screens: the result and whether it held, Pertento's drafted
 * read plus what is next, and a review of everything on one page.
 */
export const REPORT_STEPS = ['result', 'read', 'review'] as const;
export type ReportStep = (typeof REPORT_STEPS)[number];

/**
 * What the form holds. Text is '' for "no answer" rather than null so a controlled field
 * never flips between controlled and uncontrolled; choices use null for the same reason
 * nothing is selected.
 */
export type ReportDraft = {
  expectation: ReportExpectation | null;
  nextStep: ReportNextStep | null;
  outsideFactors: ReportOutsideFactor[];
  outsideNote: string;
  explanation: string;
  learning: string;
};

export type ReportAnswers = Partial<{
  expectation: ReportExpectation | null;
  nextStep: ReportNextStep | null;
  outsideFactors: ReportOutsideFactor[] | null;
  outsideNote: string | null;
  explanation: string | null;
  learning: string | null;
}>;

export const EMPTY_DRAFT: ReportDraft = {
  expectation: null,
  nextStep: null,
  outsideFactors: [],
  outsideNote: '',
  explanation: '',
  learning: '',
};

export const draftFromReport = (report: ExperimentReportSelect | null | undefined): ReportDraft => ({
  expectation: report?.expectation ?? null,
  nextStep: report?.nextStep ?? null,
  outsideFactors: report?.outsideFactors ?? [],
  outsideNote: report?.outsideNote ?? '',
  explanation: report?.explanation ?? '',
  learning: report?.learning ?? '',
});

const sameList = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((v) => b.includes(v));

/** Compared by content, as the server trims text and drops blanks, so ' x ' and 'x' are one answer. */
const sameText = (a: string, b: string) => a.trim() === b.trim();

/**
 * The answers that differ from what is saved, in the shape PUT takes. Only what changed is
 * sent: the server treats the body as a patch, so a step that touches one question never
 * rewrites the others, and a form that was never edited sends nothing at all.
 */
export const changedAnswers = (draft: ReportDraft, saved: ReportDraft): ReportAnswers => {
  const patch: ReportAnswers = {};

  if (draft.expectation !== saved.expectation) patch.expectation = draft.expectation;
  if (draft.nextStep !== saved.nextStep) patch.nextStep = draft.nextStep;
  if (!sameList(draft.outsideFactors, saved.outsideFactors)) patch.outsideFactors = draft.outsideFactors;
  if (!sameText(draft.outsideNote, saved.outsideNote)) patch.outsideNote = draft.outsideNote.trim() || null;
  if (!sameText(draft.explanation, saved.explanation)) patch.explanation = draft.explanation.trim() || null;
  if (!sameText(draft.learning, saved.learning)) patch.learning = draft.learning.trim() || null;

  return patch;
};

type RebaseParams = { draft: ReportDraft; previous: ReportDraft; next: ReportDraft };

/**
 * The draft after what is saved has changed under it: the new saved values, except for any
 * field the user has edited since the previous saved state, which keeps their edit. Without
 * this, a save response landing while someone is still typing replaced the whole draft and
 * dropped the characters typed in between.
 */
export const rebaseDraft = ({ draft, previous, next }: RebaseParams): ReportDraft => {
  const edited = changedAnswers(draft, previous);
  return {
    expectation: 'expectation' in edited ? draft.expectation : next.expectation,
    nextStep: 'nextStep' in edited ? draft.nextStep : next.nextStep,
    outsideFactors: 'outsideFactors' in edited ? draft.outsideFactors : next.outsideFactors,
    outsideNote: 'outsideNote' in edited ? draft.outsideNote : next.outsideNote,
    explanation: 'explanation' in edited ? draft.explanation : next.explanation,
    learning: 'learning' in edited ? draft.learning : next.learning,
  };
};

/**
 * The fields each modal screen owns, so saving a screen cannot send another screen's edits.
 * The review screen owns everything: it is where whatever is left gets saved.
 */
const STEP_FIELDS: Record<ReportStep, readonly (keyof ReportAnswers)[]> = {
  result: ['expectation'],
  read: ['nextStep', 'explanation', 'learning', 'outsideFactors'],
  review: ['expectation', 'nextStep', 'outsideFactors', 'outsideNote', 'explanation', 'learning'],
};

export const answersForStep = (step: ReportStep, answers: ReportAnswers): ReportAnswers =>
  Object.fromEntries(Object.entries(answers).filter(([key]) => STEP_FIELDS[step].includes(key as keyof ReportAnswers)));

export const hasAnyAnswer = (draft: ReportDraft): boolean =>
  draft.expectation !== null ||
  draft.nextStep !== null ||
  draft.outsideFactors.length > 0 ||
  !!draft.outsideNote.trim() ||
  !!draft.explanation.trim() ||
  !!draft.learning.trim();

/**
 * How many of the four groups the card is built from are answered: expectation, next step,
 * outside factors (the note only exists under "Something else", so it adds nothing alone) and
 * the lesson. The explanation is not counted, since it is the one answer
 * that is a guess rather than a fact the card can stand without.
 */
export const completeness = (draft: ReportDraft): { answered: number; total: number } => {
  const groups = [
    draft.expectation !== null,
    draft.nextStep !== null,
    draft.outsideFactors.length > 0,
    !!draft.learning.trim(),
  ];
  return { answered: groups.filter(Boolean).length, total: groups.length };
};

/**
 * How many of the five answers are given, for the Debrief tab's drawer bar. Unlike completeness
 * this counts the explanation: the bar is about whether everything has been answered, not about
 * what the card can stand without.
 */
export const answeredCount = (draft: ReportDraft): { answered: number; total: number } => {
  const groups = [
    draft.expectation !== null,
    draft.nextStep !== null,
    draft.outsideFactors.length > 0,
    !!draft.learning.trim(),
    !!draft.explanation.trim(),
  ];
  return { answered: groups.filter(Boolean).length, total: groups.length };
};

export type RowVerdict = 'right' | 'wrong' | null;

type ApplyVerdictsParams = {
  factors: readonly ReportOutsideFactor[];
  rows: readonly Pick<ReportReadRow, 'key' | 'factor' | 'claimsPresent'>[];
  verdicts: Readonly<Partial<Record<ReportReadRow['key'], RowVerdict>>>;
};

/**
 * What a confirm or a correction does to the outside factors.
 *
 * A row that asserts a factor ("mobile traffic fell 18%") switches it on when confirmed and off
 * when corrected. A row that denies one ("no tracking gaps") is the other way round: confirming
 * it means the factor is absent, correcting it means it is present. A row with no verdict is
 * left alone, and so are factors no row speaks to, so a chip ticked by hand survives.
 */
export const applyRowVerdicts = ({ factors, rows, verdicts }: ApplyVerdictsParams): ReportOutsideFactor[] => {
  const next = new Set(factors);

  for (const row of rows) {
    const verdict = verdicts[row.key];
    if (!row.factor || !verdict) continue;

    const switchedOn = row.claimsPresent ? verdict === 'right' : verdict === 'wrong';
    if (switchedOn) next.add(row.factor);
    else next.delete(row.factor);
  }

  return [...next];
};

/**
 * Whether ending an experiment should offer the wizard: only when nothing has been answered.
 * A Deployed experiment can be ended from the header after its report was written on
 * the tab, and asking the same questions again would be noise. A report row with no answers
 * cannot exist (the server refuses an empty save), but a cleared one can, so this reads the
 * answers rather than the row.
 */
export const needsReportPrompt = (report: ExperimentReportSelect | null | undefined): boolean =>
  !hasAnyAnswer(draftFromReport(report));

/** The same rule the server enforces: Ended and Deployed are both over. */
export const isConcluded = (status: string | null | undefined): boolean => status === 'Ended' || status === 'Deployed';
